import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
  Toggle,
  SelectInput,
} from "../../../components/FormField";

import { MediaImageField } from "../../../components/MediaPickerDialog";

import { showSuccess, showError } from "../../../lib/toast";

import { emptyButton, moveItem } from "./shared";

import {
  server_post_data,
  dynamic_page_save_update,
} from "../../../serviceconnection/ServiceConnection";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function HomeSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroSlides, setHeroSlides] = useState([
    {
      video: "",
      headline: "",
      highlightText: "",
      headlineEnd: "",
      description: "",
      ctaText: "",
      ctaLink: "",
    },
  ]);

  /* ---------------- SOLUTIONS ---------------- */
  const [solActive, setSolActive] = useState(true);
  const [solHeadTag, setSolHeadTag] = useState("");
  const [solHeading, setSolHeading] = useState("");
  const [solHeadingEmphasis, setSolHeadingEmphasis] = useState("");
  const [solCards, setSolCards] = useState([
    {
      title: "",
      description: "",
      label: "",
      imageUrl: "",
      imageAlt: "",
      linkUrl: "",
      featured: false,
      ctaText: "",
      ctaLink: "",
    },
  ]);

  /* ---------------- INDUSTRIES ---------------- */
  const [indActive, setIndActive] = useState(true);
  const [indHeadTag, setIndHeadTag] = useState("");
  const [indHeading, setIndHeading] = useState("");
  const [indHeadingEmphasis, setIndHeadingEmphasis] = useState("");
  const [indCTAText, setIndCTAText] = useState("");
  const [indCTALink, setIndCTALink] = useState("");

  const [indCards, setIndCards] = useState([
    {
      icon: "",
      title: "",
      tagline: "",
      gradient: "",
      bgColor: "",
      linkUrl: "",
      ctaText: "",
      ctaLink: "",
    },
  ]);

  /* ---------------- SERVICES ---------------- */
  const [svcActive, setSvcActive] = useState(true);
  const [svcHeadTag, setSvcHeadTag] = useState("");
  const [svcHeading, setSvcHeading] = useState("");
  const [svcHeadingEmphasis, setSvcHeadingEmphasis] = useState("");
  const [svcDesc, setSvcDesc] = useState("");
  const [svcCTAText, setSvcCTAText] = useState("");
  const [svcCTALink, setSvcCTALink] = useState("");
  const [svcCards, setSvcCards] = useState([
    {
      title: "",
      description: "",
      label: "",
      icon: "",
      imageUrl: "",
      imageAlt: "",
      linkUrl: "",
      ctaText: "",
      ctaLink: "",
    },
  ]);

  /* ---------------- PRODUCTS ---------------- */
  const [prodActive, setProdActive] = useState(true);
  const [prodHeadTag, setProdHeadTag] = useState("");
  const [prodHeading, setProdHeading] = useState("");
  const [prodHeadingEmphasis, setProdHeadingEmphasis] = useState("");
  const [prodDesc, setProdDesc] = useState("");
  const [prodCTAText, setProdCTAText] = useState("");
  const [prodCTALink, setProdCTALink] = useState("");
  const [prodCards, setProdCards] = useState([
    {
      title: "",
      description: "",
      category: "",
      icon: "",
      imageUrl: "",
      imageAlt: "",
      linkUrl: "",
      featured: false,
      ctaText: "",
      ctaLink: "",
    },
  ]);

  /* ---------------- OUR DIFFERENCE ---------------- */
  const [whyActive, setWhyActive] = useState(true);
  const [whyHeadTag, setWhyHeadTag] = useState("");
  const [whyHeading, setWhyHeading] = useState("");
  const [whyHeadingEmphasis, setWhyHeadingEmphasis] = useState("");
  const [whyDesc, setWhyDesc] = useState("");
  const [whyCTAText, setWhyCTAText] = useState("");
  const [whyCTALink, setWhyCTALink] = useState("");
  const [whyCards, setWhyCards] = useState([
    {
      title: "",
      description: "",
      imageUrl: "",
      imageAlt: "",
      overlayGradient: "",
      ctaText: "",
      ctaLink: "",
    },
  ]);

  /* ---------------- TRUSTED BY ---------------- */
  const [trustedActive, setTrustedActive] = useState(true);
  const [trustedHeading, setTrustedHeading] = useState("");
  const [trustedHeadingEmphasis, setTrustedHeadingEmphasis] = useState("");
  const [trustedDesc, setTrustedDesc] = useState("");
  const [trustedCards, setTrustedCards] = useState([
    { name: "", subtitle: "", logoUrl: "" },
  ]);

  /* ---------------- CLIENT SUCCESS ---------------- */
  const [csActive, setCsActive] = useState(true);
  const [csHeading, setCsHeading] = useState("");
  const [csCTAText, setCsCTAText] = useState("");
  const [csCTALink, setCsCTALink] = useState("");
  const [testimonials, setTestimonials] = useState([
    { quote: "", author: "", company: "", kpis: "" },
  ]);

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeadTag, setCtaHeadTag] = useState("");
  const [ctaHeading, setCtaHeading] = useState("");
  const [ctaHeadingEmphasis, setCtaHeadingEmphasis] = useState("");
  const [ctaBgImage, setCtaBgImage] = useState("");
  const [ctaContactPhone, setCtaContactPhone] = useState("");
  const [ctaContactEmail, setCtaContactEmail] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState(emptyButton());
  const [ctaSecondary, setCtaSecondary] = useState(emptyButton());

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.heroSlides?.length) setHeroSlides(d.heroSlides);

    if (d.solutions) {
      setSolHeadTag(d.solutions.head_tag || "");
      setSolHeading(d.solutions.heading || "");
      setSolHeadingEmphasis(d.solutions.heading_emphasis || "");
      if (d.solutions.cards?.length) setSolCards(d.solutions.cards);
    }

    if (d.industries) {
      setIndHeadTag(d.industries.head_tag || "");
      setIndHeading(d.industries.heading || "");
      setIndHeadingEmphasis(d.industries.heading_emphasis || "");
      setIndCTAText(d.industries.cta_text || "");
      setIndCTALink(d.industries.cta_link || "");
      if (d.industries.cards?.length) setIndCards(d.industries.cards);
    }

    if (d.services) {
      setSvcHeadTag(d.services.head_tag || "");
      setSvcHeading(d.services.heading || "");
      setSvcHeadingEmphasis(d.services.heading_emphasis || "");
      setSvcDesc(d.services.description || "");
      setSvcCTAText(d.services.cta_text || "");
      setSvcCTALink(d.services.cta_link || "");
      if (d.services.cards?.length) setSvcCards(d.services.cards);
    }

    if (d.products) {
      setProdHeadTag(d.products.head_tag || "");
      setProdHeading(d.products.heading || "");
      setProdHeadingEmphasis(d.products.heading_emphasis || "");
      setProdDesc(d.products.description || "");
      setProdCTAText(d.products.cta_text || "");
      setProdCTALink(d.products.cta_link || "");
      if (d.products.cards?.length) setProdCards(d.products.cards);
    }

    if (d.our_difference) {
      setWhyHeadTag(d.our_difference.head_tag || "");
      setWhyHeading(d.our_difference.heading || "");
      setWhyHeadingEmphasis(d.our_difference.heading_emphasis || "");
      setWhyDesc(d.our_difference.description || "");
      setWhyCTAText(d.our_difference.cta_text || "");
      setWhyCTALink(d.our_difference.cta_link || "");
      if (d.our_difference.cards?.length) setWhyCards(d.our_difference.cards);
    }

    if (d.trusted_by) {
      setTrustedHeading(d.trusted_by.heading || "");
      setTrustedHeadingEmphasis(d.trusted_by.heading_emphasis || "");
      setTrustedDesc(d.trusted_by.description || "");
      if (d.trusted_by.cards?.length) setTrustedCards(d.trusted_by.cards);
    }

    if (d.client_success) {
      setCsHeading(d.client_success.heading || "");
      setCsCTAText(d.client_success.cta_text || "");
      setCsCTALink(d.client_success.cta_link || "");
      if (d.client_success.cards?.length)
        setTestimonials(d.client_success.cards);
    }

    if (d.get_started_today) {
      const cta = d.get_started_today;
      setCtaHeadTag(cta.head_tag || "");
      setCtaHeading(cta.heading || "");
      setCtaHeadingEmphasis(cta.heading_emphasis || "");
      setCtaBgImage(cta.bg_image || "");
      setCtaContactPhone(cta.contact_phone || "");
      setCtaContactEmail(cta.contact_email || "");
      if (cta.primary_cta)
        setCtaPrimary({
          text: cta.primary_cta.text || "",
          url: cta.primary_cta.link || "",
          newTab: false,
        });
      if (cta.secondary_cta)
        setCtaSecondary({
          text: cta.secondary_cta.text || "",
          url: cta.secondary_cta.link || "",
          newTab: false,
        });
    }
  }, [initialData]);

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  const buildPayload = () => ({
    p_id: 0,
    page_name: "home",
    page_type: "home",
    category: "website",
    sections: {
      heroSlides,
      solutions: {
        head_tag: solHeadTag,
        heading: solHeading,
        heading_emphasis: solHeadingEmphasis,
        cards: solCards,
      },
      industries: {
        head_tag: indHeadTag,
        heading: indHeading,
        heading_emphasis: indHeadingEmphasis,
        cards: indCards,
        cta_text: indCTAText,
        cta_link: indCTALink,
      },
      services: {
        head_tag: svcHeadTag,
        heading: svcHeading,
        heading_emphasis: svcHeadingEmphasis,
        description: svcDesc,
        cards: svcCards,
        cta_text: svcCTAText,
        cta_link: svcCTALink,
      },
      products: {
        head_tag: prodHeadTag,
        heading: prodHeading,
        heading_emphasis: prodHeadingEmphasis,
        description: prodDesc,
        cards: prodCards,
        cta_text: prodCTAText,
        cta_link: prodCTALink,
      },
      our_difference: {
        head_tag: whyHeadTag,
        heading: whyHeading,
        heading_emphasis: whyHeadingEmphasis,
        description: whyDesc,
        cards: whyCards,
        cta_text: whyCTAText,
        cta_link: whyCTALink,
      },
      trusted_by: {
        heading: trustedHeading,
        heading_emphasis: trustedHeadingEmphasis,
        description: trustedDesc,
        cards: trustedCards,
      },
      client_success: {
        heading: csHeading,
        cards: testimonials,
        cta_text: csCTAText,
        cta_link: csCTALink,
      },
      get_started_today: {
        head_tag: ctaHeadTag,
        heading: ctaHeading,
        heading_emphasis: ctaHeadingEmphasis,
        bg_image: ctaBgImage,
        primary_cta: { text: ctaPrimary.text, link: ctaPrimary.url },
        secondary_cta: { text: ctaSecondary.text, link: ctaSecondary.url },
        contact_phone: ctaContactPhone,
        contact_email: ctaContactEmail,
        stats_highlight: [
          { icon: "Phone", value: ctaContactPhone },
          { icon: "Email", value: ctaContactEmail },
        ],
      },
    },
  });

  useEffect(() => {
    const payload = buildPayload();
    setSectionsData?.(payload.sections);
  }, [
    heroSlides,
    solHeadTag,
    solHeading,
    solHeadingEmphasis,
    solCards,
    indHeadTag,
    indHeading,
    indHeadingEmphasis,
    indCards,
    indCTAText,
    indCTALink,
    svcHeadTag,
    svcHeading,
    svcHeadingEmphasis,
    svcDesc,
    svcCTAText,
    svcCTALink,
    svcCards,
    prodHeadTag,
    prodHeading,
    prodHeadingEmphasis,
    prodDesc,
    prodCTAText,
    prodCTALink,
    prodCards,
    whyHeadTag,
    whyHeading,
    whyHeadingEmphasis,
    whyDesc,
    whyCTAText,
    whyCTALink,
    whyCards,
    trustedHeading,
    trustedHeadingEmphasis,
    trustedDesc,
    trustedCards,
    csHeading,
    csCTAText,
    csCTALink,
    testimonials,
    ctaHeadTag,
    ctaHeading,
    ctaHeadingEmphasis,
    ctaBgImage,
    ctaContactPhone,
    ctaContactEmail,
    ctaPrimary,
    ctaSecondary,
  ]);

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <div className="flex justify-between mb-4">
          <p className="text-xs font-semibold uppercase">Slides</p>
          <button
            onClick={() =>
              setHeroSlides((s) => [
                ...s,
                {
                  video: "",
                  headline: "",
                  highlightText: "",
                  headlineEnd: "",
                  description: "",
                  ctaText: "",
                  ctaLink: "",
                },
              ])
            }
            className="flex gap-1 items-center text-xs"
          >
            <Plus size={14} /> Add Slide
          </button>
        </div>
        {heroSlides.map((slide, i) => (
          <div key={i} className="border p-4 rounded-lg mb-4 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Slide {i + 1}</p>
              {heroSlides.length > 1 && (
                <button
                  onClick={() =>
                    setHeroSlides((s) => s.filter((_, idx) => idx !== i))
                  }
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <Field label="Hero Video">
              <MediaImageField
                label="Select Hero Video"
                value={slide.video}
                onChange={(v) =>
                  setHeroSlides((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, video: v } : x)),
                  )
                }
              />
            </Field>
            <Field label="Headline">
              <TextInput
                value={slide.headline}
                onChange={(e) =>
                  setHeroSlides((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, headline: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Highlight Text">
              <TextInput
                value={slide.highlightText}
                onChange={(e) =>
                  setHeroSlides((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, highlightText: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Headline End">
              <TextInput
                value={slide.headlineEnd}
                onChange={(e) =>
                  setHeroSlides((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, headlineEnd: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={slide.description}
                onChange={(e) =>
                  setHeroSlides((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA Text">
                <TextInput
                  value={slide.ctaText}
                  onChange={(e) =>
                    setHeroSlides((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaText: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="CTA Link">
                <TextInput
                  value={slide.ctaLink}
                  onChange={(e) =>
                    setHeroSlides((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaLink: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* SOLUTIONS */}
      <CollapsibleSection
        title="Solutions"
        active={solActive}
        onToggleActive={setSolActive}
      >
        <Field label="Head Tag">
          <TextInput
            value={solHeadTag}
            onChange={(e) => setSolHeadTag(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={solHeading}
            onChange={(e) => setSolHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={solHeadingEmphasis}
            onChange={(e) => setSolHeadingEmphasis(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() =>
              setSolCards((s) => [
                ...s,
                {
                  title: "",
                  description: "",
                  label: "",
                  imageUrl: "",
                  imageAlt: "",
                  linkUrl: "",
                  featured: false,
                  ctaText: "",
                  ctaLink: "",
                },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {solCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={solCards.length}
                  onMove={(f, t) => setSolCards(moveItem(solCards, f, t))}
                />
                {solCards.length > 1 && (
                  <button
                    onClick={() =>
                      setSolCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setSolCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setSolCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Label">
              <TextInput
                value={c.label}
                onChange={(e) =>
                  setSolCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, label: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Featured">
              <Toggle
                checked={c.featured}
                onChange={(v) =>
                  setSolCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, featured: v } : x)),
                  )
                }
              />
            </Field>
            <Field label="Image">
              <MediaImageField
                label="Upload Image"
                value={c.imageUrl}
                onChange={(v) =>
                  setSolCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, imageUrl: v } : x)),
                  )
                }
                alt={c.imageAlt}
                onAltChange={(v) =>
                  setSolCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, imageAlt: v } : x)),
                  )
                }
              />
            </Field>
            <Field label="Link URL">
              <TextInput
                value={c.linkUrl}
                onChange={(e) =>
                  setSolCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, linkUrl: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA Text">
                <TextInput
                  value={c.ctaText}
                  onChange={(e) =>
                    setSolCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaText: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="CTA Link">
                <TextInput
                  value={c.ctaLink}
                  onChange={(e) =>
                    setSolCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaLink: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* INDUSTRIES */}
      <CollapsibleSection
        title="Industries"
        active={indActive}
        onToggleActive={setIndActive}
      >
        <Field label="Head Tag">
          <TextInput
            value={indHeadTag}
            onChange={(e) => setIndHeadTag(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={indHeading}
            onChange={(e) => setIndHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={indHeadingEmphasis}
            onChange={(e) => setIndHeadingEmphasis(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={indCTAText}
              onChange={(e) => setIndCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={indCTALink}
              onChange={(e) => setIndCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Industry Cards</p>
          <button
            onClick={() =>
              setIndCards((s) => [
                ...s,
                {
                  icon: "",
                  title: "",
                  tagline: "",
                  gradient: "",
                  bgColor: "",
                  linkUrl: "",
                  ctaText: "",
                  ctaLink: "",
                },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {indCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Industry {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={indCards.length}
                  onMove={(f, t) => setIndCards(moveItem(indCards, f, t))}
                />
                {indCards.length > 1 && (
                  <button
                    onClick={() =>
                      setIndCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Tagline">
              <TextInput
                value={c.tagline}
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, tagline: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Gradient">
              <TextInput
                value={c.gradient}
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, gradient: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Background Color">
              <TextInput
                value={c.bgColor}
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, bgColor: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Link URL">
              <TextInput
                value={c.linkUrl}
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, linkUrl: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA Text">
                <TextInput
                  value={c.ctaText}
                  onChange={(e) =>
                    setIndCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaText: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="CTA Link">
                <TextInput
                  value={c.ctaLink}
                  onChange={(e) =>
                    setIndCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaLink: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* SERVICES */}
      <CollapsibleSection
        title="Services"
        active={svcActive}
        onToggleActive={setSvcActive}
      >
        <Field label="Head Tag">
          <TextInput
            value={svcHeadTag}
            onChange={(e) => setSvcHeadTag(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={svcHeading}
            onChange={(e) => setSvcHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={svcHeadingEmphasis}
            onChange={(e) => setSvcHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={svcDesc}
            onChange={(e) => setSvcDesc(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Section CTA Text">
            <TextInput
              value={svcCTAText}
              onChange={(e) => setSvcCTAText(e.target.value)}
            />
          </Field>
          <Field label="Section CTA Link">
            <TextInput
              value={svcCTALink}
              onChange={(e) => setSvcCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() =>
              setSvcCards((s) => [
                ...s,
                {
                  title: "",
                  description: "",
                  label: "",
                  icon: "",
                  imageUrl: "",
                  imageAlt: "",
                  linkUrl: "",
                  ctaText: "",
                  ctaLink: "",
                },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {svcCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={svcCards.length}
                  onMove={(f, t) => setSvcCards(moveItem(svcCards, f, t))}
                />
                {svcCards.length > 1 && (
                  <button
                    onClick={() =>
                      setSvcCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setSvcCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setSvcCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Label">
              <TextInput
                value={c.label}
                onChange={(e) =>
                  setSvcCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, label: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setSvcCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Image">
              <MediaImageField
                label="Upload Image"
                value={c.imageUrl}
                onChange={(v) =>
                  setSvcCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, imageUrl: v } : x)),
                  )
                }
                alt={c.imageAlt}
                onAltChange={(v) =>
                  setSvcCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, imageAlt: v } : x)),
                  )
                }
              />
            </Field>
            <Field label="Link URL">
              <TextInput
                value={c.linkUrl}
                onChange={(e) =>
                  setSvcCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, linkUrl: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA Text">
                <TextInput
                  value={c.ctaText}
                  onChange={(e) =>
                    setSvcCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaText: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="CTA Link">
                <TextInput
                  value={c.ctaLink}
                  onChange={(e) =>
                    setSvcCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaLink: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* PRODUCTS */}
      <CollapsibleSection
        title="Products"
        active={prodActive}
        onToggleActive={setProdActive}
      >
        <Field label="Head Tag">
          <TextInput
            value={prodHeadTag}
            onChange={(e) => setProdHeadTag(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={prodHeading}
            onChange={(e) => setProdHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={prodHeadingEmphasis}
            onChange={(e) => setProdHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={prodDesc}
            onChange={(e) => setProdDesc(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Section CTA Text">
            <TextInput
              value={prodCTAText}
              onChange={(e) => setProdCTAText(e.target.value)}
            />
          </Field>
          <Field label="Section CTA Link">
            <TextInput
              value={prodCTALink}
              onChange={(e) => setProdCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() =>
              setProdCards((s) => [
                ...s,
                {
                  title: "",
                  description: "",
                  category: "",
                  icon: "",
                  imageUrl: "",
                  imageAlt: "",
                  linkUrl: "",
                  featured: false,
                  ctaText: "",
                  ctaLink: "",
                },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {prodCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={prodCards.length}
                  onMove={(f, t) => setProdCards(moveItem(prodCards, f, t))}
                />
                {prodCards.length > 1 && (
                  <button
                    onClick={() =>
                      setProdCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setProdCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setProdCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Category">
              <TextInput
                value={c.category}
                onChange={(e) =>
                  setProdCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, category: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setProdCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Featured">
              <Toggle
                checked={c.featured}
                onChange={(v) =>
                  setProdCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, featured: v } : x)),
                  )
                }
              />
            </Field>
            <Field label="Image">
              <MediaImageField
                label="Upload Image"
                value={c.imageUrl}
                onChange={(v) =>
                  setProdCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, imageUrl: v } : x)),
                  )
                }
                alt={c.imageAlt}
                onAltChange={(v) =>
                  setProdCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, imageAlt: v } : x)),
                  )
                }
              />
            </Field>
            <Field label="Link URL">
              <TextInput
                value={c.linkUrl}
                onChange={(e) =>
                  setProdCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, linkUrl: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA Text">
                <TextInput
                  value={c.ctaText}
                  onChange={(e) =>
                    setProdCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaText: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="CTA Link">
                <TextInput
                  value={c.ctaLink}
                  onChange={(e) =>
                    setProdCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaLink: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* OUR DIFFERENCE */}
      <CollapsibleSection
        title="Our Difference"
        active={whyActive}
        onToggleActive={setWhyActive}
      >
        <Field label="Head Tag">
          <TextInput
            value={whyHeadTag}
            onChange={(e) => setWhyHeadTag(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={whyHeading}
            onChange={(e) => setWhyHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={whyHeadingEmphasis}
            onChange={(e) => setWhyHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={whyDesc}
            onChange={(e) => setWhyDesc(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Section CTA Text">
            <TextInput
              value={whyCTAText}
              onChange={(e) => setWhyCTAText(e.target.value)}
            />
          </Field>
          <Field label="Section CTA Link">
            <TextInput
              value={whyCTALink}
              onChange={(e) => setWhyCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() =>
              setWhyCards((s) => [
                ...s,
                {
                  title: "",
                  description: "",
                  imageUrl: "",
                  imageAlt: "",
                  overlayGradient: "",
                  ctaText: "",
                  ctaLink: "",
                },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {whyCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={whyCards.length}
                  onMove={(f, t) => setWhyCards(moveItem(whyCards, f, t))}
                />
                {whyCards.length > 1 && (
                  <button
                    onClick={() =>
                      setWhyCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Image">
              <MediaImageField
                label="Upload Image"
                value={c.imageUrl}
                onChange={(v) =>
                  setWhyCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, imageUrl: v } : x)),
                  )
                }
                alt={c.imageAlt}
                onAltChange={(v) =>
                  setWhyCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, imageAlt: v } : x)),
                  )
                }
              />
            </Field>
            <Field label="Overlay Gradient">
              <TextInput
                value={c.overlayGradient}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, overlayGradient: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA Text">
                <TextInput
                  value={c.ctaText}
                  onChange={(e) =>
                    setWhyCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaText: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="CTA Link">
                <TextInput
                  value={c.ctaLink}
                  onChange={(e) =>
                    setWhyCards((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, ctaLink: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* TRUSTED BY */}
      <CollapsibleSection
        title="Trusted By"
        active={trustedActive}
        onToggleActive={setTrustedActive}
      >
        <Field label="Heading">
          <TextInput
            value={trustedHeading}
            onChange={(e) => setTrustedHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={trustedHeadingEmphasis}
            onChange={(e) => setTrustedHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={trustedDesc}
            onChange={(e) => setTrustedDesc(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Logos</p>
          <button
            onClick={() =>
              setTrustedCards((s) => [
                ...s,
                { name: "", subtitle: "", logoUrl: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Logo
          </button>
        </div>
        {trustedCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Logo {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={trustedCards.length}
                  onMove={(f, t) =>
                    setTrustedCards(moveItem(trustedCards, f, t))
                  }
                />
                {trustedCards.length > 1 && (
                  <button
                    onClick={() =>
                      setTrustedCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Name">
              <TextInput
                value={c.name}
                onChange={(e) =>
                  setTrustedCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, name: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Subtitle">
              <TextInput
                value={c.subtitle}
                onChange={(e) =>
                  setTrustedCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, subtitle: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Logo">
              <MediaImageField
                label="Select Logo"
                value={c.logoUrl}
                onChange={(v) =>
                  setTrustedCards((s) =>
                    s.map((x, idx) => (idx === i ? { ...x, logoUrl: v } : x)),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* CLIENT SUCCESS */}
      <CollapsibleSection
        title="Client Success"
        active={csActive}
        onToggleActive={setCsActive}
      >
        <Field label="Heading">
          <TextInput
            value={csHeading}
            onChange={(e) => setCsHeading(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Section CTA Text">
            <TextInput
              value={csCTAText}
              onChange={(e) => setCsCTAText(e.target.value)}
            />
          </Field>
          <Field label="Section CTA Link">
            <TextInput
              value={csCTALink}
              onChange={(e) => setCsCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Testimonials</p>
          <button
            onClick={() =>
              setTestimonials((s) => [
                ...s,
                { quote: "", author: "", company: "", kpis: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Testimonial
          </button>
        </div>
        {testimonials.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Testimonial {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={testimonials.length}
                  onMove={(f, t) =>
                    setTestimonials(moveItem(testimonials, f, t))
                  }
                />
                {testimonials.length > 1 && (
                  <button
                    onClick={() =>
                      setTestimonials((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Quote">
              <TextArea
                value={c.quote}
                onChange={(e) =>
                  setTestimonials((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, quote: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Author">
              <TextInput
                value={c.author}
                onChange={(e) =>
                  setTestimonials((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, author: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Company">
              <TextInput
                value={c.company}
                onChange={(e) =>
                  setTestimonials((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, company: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="KPIs (comma separated)">
              <TextInput
                value={c.kpis}
                onChange={(e) =>
                  setTestimonials((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, kpis: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* GET STARTED TODAY */}
      <CollapsibleSection
        title="Get Started Today"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Head Tag">
          <TextInput
            value={ctaHeadTag}
            onChange={(e) => setCtaHeadTag(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={ctaHeadingEmphasis}
            onChange={(e) => setCtaHeadingEmphasis(e.target.value)}
          />
        </Field>

        <Field label="Background Image">
          <MediaImageField
            label="Select Background Image"
            value={ctaBgImage}
            onChange={setCtaBgImage}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Contact Phone">
            <TextInput
              value={ctaContactPhone}
              onChange={(e) => setCtaContactPhone(e.target.value)}
            />
          </Field>
          <Field label="Contact Email">
            <TextInput
              value={ctaContactEmail}
              onChange={(e) => setCtaContactEmail(e.target.value)}
            />
          </Field>
        </div>

        <CTAGroup
          label="Primary CTA"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          newTab={ctaSecondary.newTab}
          onTextChange={(v) => updateBtn(setCtaSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaSecondary, "newTab", v)}
        />
      </CollapsibleSection>
    </>
  );
}
